import { CanonicalSearchValues } from '../../patterns/saved-searches/save-search'
import { canonicalSearchValuesParser } from './canonical-search-values-parser.util'
import { SearchValue } from '../../components/save-search/save-a-search/save-search-form'

export const CAPITALISED_MAKE_EXCEPTIONS: string[] = [
    'AC',
    'BMW',
    'DAX',
    'KTM',
    'LTI',
    'MINI',
    'MK',
    'NSU',
    'TVR',
    'SKODA',
]

export const capitaliseWord = (searchValue: string): string => {
    const findSeparator = /[\s-_]+/
    const joiner = searchValue.match(findSeparator) || ['']
    return searchValue
        .toLowerCase()
        .split(findSeparator)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(joiner[0])
}

export const formatNumber = (priceString: string): string => {
    const intPrice = Number(priceString)
    if (!intPrice) return ''

    if (intPrice < 1000) {
        return `${intPrice}`
    } else if (intPrice < 1000000) {
        return `${intPrice / 1000}K`
    } else {
        return `${intPrice / 1000000}M`
    }
}

export const formatRange = (from: string, to: string, prefixUnit: string, suffixUnit: string): string => {
    if (!from) {
        return `Up to ${prefixUnit}${to}${suffixUnit}`
    }
    if (!to) {
        return `From ${prefixUnit}${from}${suffixUnit}`
    }

    return `${prefixUnit}${from}${suffixUnit} - ${prefixUnit}${to}${suffixUnit}`
}

export const findSavedCanonicalSearchKeys = (formatterKeys: string[], canonicalSearchValues: CanonicalSearchValues) => {
    return formatterKeys.filter((formatterKey) => Object.keys(canonicalSearchValues).includes(formatterKey))
}

export const mapSearchKeysForRange = (searchKeys: string[]) => {
    const keysWithMin = searchKeys
        .filter((key) => key.startsWith('MIN_') || key.startsWith('MINIMUM_') || key.endsWith('_FROM'))
        .map((key) => key.replace('MIN_', '').replace('MINIMUM_', '').replace('_FROM', ''))
    const keysWithMax = searchKeys
        .filter((key) => key.startsWith('MAX_') || key.startsWith('MAXIMUM_') || key.endsWith('_TO'))
        .map((key) => key.replace('MAX_', '').replace('MAXIMUM_', '').replace('_TO', ''))
    const keysForRange: string[] = []
    let searchKeysToMap: string[] = searchKeys
    for (const key of keysWithMin) {
        if (keysWithMax.includes(key)) {
            keysForRange.push(key)
            searchKeysToMap = searchKeysToMap.filter(
                (searchKey) =>
                    searchKey !== `MIN_${key}` &&
                    searchKey !== `MAX_${key}` &&
                    searchKey !== `${key}_FROM` &&
                    searchKey !== `${key}_TO` &&
                    searchKey !== `MINIMUM_${key}` &&
                    searchKey !== `MAXIMUM_${key}`
            )
        }
    }

    return searchKeysToMap.concat(keysForRange)
}

export const mapCanonicalValuesForRange = (canonicalSearchValues: CanonicalSearchValues): CanonicalSearchValues => {
    const canonicalValuesForRange: CanonicalSearchValues = {}
    const keysWithMin = Object.keys(canonicalSearchValues)
        .filter((key) => key.startsWith('MIN_'))
        .map((key) => key.replace('MIN_', ''))
    const keysWithMinimum = Object.keys(canonicalSearchValues)
        .filter((key) => key.startsWith('MINIMUM_'))
        .map((key) => key.replace('MINIMUM_', ''))
    const keysWithFrom = Object.keys(canonicalSearchValues)
        .filter((key) => key.endsWith('_FROM'))
        .map((key) => key.replace('_FROM', ''))
    const keysWithMax = Object.keys(canonicalSearchValues)
        .filter((key) => key.startsWith('MAX_'))
        .map((key) => key.replace('MAX_', ''))
    const keysWithMaximum = Object.keys(canonicalSearchValues)
        .filter((key) => key.startsWith('MAXIMUM_'))
        .map((key) => key.replace('MAXIMUM_', ''))
    const keysWithTo = Object.keys(canonicalSearchValues)
        .filter((key) => key.endsWith('_TO'))
        .map((key) => key.replace('_TO', ''))
    for (const key of keysWithMin) {
        if (keysWithMax.includes(key)) {
            canonicalValuesForRange[key] = {
                from: String(canonicalSearchValues[`MIN_${key}`]),
                to: String(canonicalSearchValues[`MAX_${key}`]),
            }
        }
    }
    for (const key of keysWithMinimum) {
        if (keysWithMaximum.includes(key)) {
            canonicalValuesForRange[key] = {
                from: String(canonicalSearchValues[`MINIMUM_${key}`]),
                to: String(canonicalSearchValues[`MAXIMUM_${key}`]),
            }
        }
    }
    for (const key of keysWithFrom) {
        if (keysWithTo.includes(key)) {
            canonicalValuesForRange[key] = {
                from: String(canonicalSearchValues[`${key}_FROM`]),
                to: String(canonicalSearchValues[`${key}_TO`]),
            }
        }
    }

    return { ...canonicalSearchValues, ...canonicalValuesForRange }
}

export const searchKeysToFormattedValues = (searchKeys: string[], canonicalSearchValues: CanonicalSearchValues) => {
    const searchKeysWithRange = mapSearchKeysForRange(searchKeys)
    const searchValuesWithRange = mapCanonicalValuesForRange(canonicalSearchValues)
    const formattedValues = searchKeysWithRange.map((canonicalSearchKey: string) => {
        const rawValue = searchValuesWithRange[canonicalSearchKey]
        return canonicalSearchValuesParser[canonicalSearchKey].getFormattedValue(rawValue as SearchValue)
    })
    return !searchKeys.includes('RADIUS') ? ['National', ...formattedValues] : formattedValues
}
